/**
 * Top navigation bar.
 *
 * Visually polished but structurally identical to the previous version
 * (no router / page changes). Active route is highlighted with the
 * accent colour, the brand mark sits left, on small screens we drop
 * to a right-anchored Drawer.
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  Store as StoreIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Insights as InsightsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Subscriptions as SubscriptionsIcon,
  AutoGraph as AutoGraphIcon,
} from '@mui/icons-material';

const MENU = [
  { label: 'Dashboard',         path: '/dashboard',                icon: <DashboardIcon /> },
  { label: 'Store Tracking',    path: '/store-tracking',           icon: <StoreIcon /> },
  { label: 'Subscriptions',     path: '/app-store-subscriptions',  icon: <SubscriptionsIcon /> },
  { label: 'Web Analytics',     path: '/app-metrics',              icon: <AnalyticsIcon /> },
  { label: 'Unified Analytics', path: '/unified-analytics',        icon: <InsightsIcon /> },
  { label: 'Social Media',      path: '/social-media',             icon: <TrendingUpIcon /> },
  { label: 'Admin',             path: '/admin-panels',             icon: <AdminPanelSettingsIcon /> },
];

const NavLink = ({ item, active, onClick }) => {
  const theme = useTheme();
  return (
    <Box
      role="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        cursor: 'pointer',
        userSelect: 'none',
        fontSize: 13,
        fontWeight: 600,
        color: active ? theme.palette.primary.main : theme.palette.text.secondary,
        backgroundColor: active
          ? alpha(theme.palette.primary.main, 0.1)
          : 'transparent',
        transition: 'all 120ms ease',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
        },
        '& svg': { fontSize: 18 },
      }}
    >
      {item.icon}
      {item.label}
    </Box>
  );
};

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNav = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <AppBar position="sticky" color="default">
      <Toolbar sx={{ minHeight: { xs: 60, md: 68 }, px: { xs: 2, md: 3 }, gap: 2 }}>
        {/* Brand */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer', flexShrink: 0 }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <AutoGraphIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              Marketing Dashboard
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
              collabry.io
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ flex: 1 }} />

        {isMobile ? (
          <>
            <IconButton onClick={() => setMobileOpen(true)} aria-label="open menu">
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              PaperProps={{ sx: { width: 300, borderTopLeftRadius: 12, borderBottomLeftRadius: 12 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Menu
                </Typography>
                <IconButton onClick={() => setMobileOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Divider />
              <List sx={{ p: 1 }}>
                {MENU.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <ListItemButton
                      key={item.path}
                      selected={active}
                      onClick={() => handleNav(item.path)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
                    </ListItemButton>
                  );
                })}
              </List>
            </Drawer>
          </>
        ) : (
          <Stack direction="row" spacing={0.5}>
            {MENU.map((item) => (
              <NavLink
                key={item.path}
                item={item}
                active={location.pathname === item.path}
                onClick={() => navigate(item.path)}
              />
            ))}
          </Stack>
        )}
      </Toolbar>
    </AppBar>
  );
}
